import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../i18n";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = loc.pathname === "/";
  const light = onHome && !scrolled;

  const links = [
    { id: "story", href: "#story", label: t.nav.story },
    { id: "route", href: "#route", label: t.nav.route },
    { id: "different", href: "#different", label: t.nav.different },
    { id: "pricing", href: "#pricing", label: t.nav.pricing },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#FDFAF3]/85 backdrop-blur-xl border-b border-[#E5D9C0]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-baseline">
          <span className={`font-serif-display text-base sm:text-lg md:text-xl tracking-tight transition-colors whitespace-nowrap ${light ? "text-[#FDFAF3]" : "text-[#2D3640]"}`}>
            Porto Beyond the Postcard
          </span>
        </Link>

        {loc.pathname === "/" && (
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a key={l.id} href={l.href} data-testid={`nav-${l.id}`}
                className={`text-sm tracking-wide transition-colors ${light ? "text-[#F7F1E8]/85 hover:text-white" : "text-[#4A5A6B] hover:text-[#5E7891]"}`}>
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-3">
          <div data-testid="lang-toggle" className="hidden sm:flex items-center gap-1 text-xs tracking-widest">
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded-full transition ${lang === "en" ? "bg-[#8D869B] text-[#FDFAF3]" : light ? "text-[#F7F1E8]/80 hover:text-white" : "text-[#5E7891] hover:text-[#2D3640]"}`}
            >EN</button>
            <span className={light ? "text-[#E7C9A4]" : "text-[#C98C97]"}>·</span>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2 py-1 rounded-full transition ${lang === "es" ? "bg-[#8D869B] text-[#FDFAF3]" : light ? "text-[#F7F1E8]/80 hover:text-white" : "text-[#5E7891] hover:text-[#2D3640]"}`}
            >ES</button>
          </div>
          <a
            data-testid="header-book-cta"
            href="#booking"
            className={`btn-sweep hidden sm:inline-flex items-center px-5 py-2.5 rounded-full text-sm tracking-wide transition-colors ${light ? "bg-[#FDFAF3] text-[#2D3640] hover:bg-[#E7C9A4]" : "bg-[#2D3640] text-[#FDFAF3] hover:bg-[#5E7891]"}`}
          >
            {t.nav.book}
          </a>
          <button data-testid="mobile-menu-toggle" className={`md:hidden p-2 transition-colors ${light ? "text-[#FDFAF3]" : "text-[#2D3640]"}`} onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-[#E5D9C0] bg-[#FDFAF3]/95 backdrop-blur-xl">
          <div className="px-6 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <a key={l.id} href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`}
                className="text-[#4A5A6B] hover:text-[#2D3640]">{l.label}</a>
            ))}
            <a href="#booking" onClick={() => setOpen(false)} data-testid="mobile-book"
              className="mt-2 inline-flex justify-center px-5 py-2.5 rounded-full bg-[#2D3640] text-[#FDFAF3] text-sm tracking-wide">
              {t.nav.book}
            </a>
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-full text-xs ${lang==='en'?'bg-[#8D869B] text-[#FDFAF3]':'text-[#5E7891]'}`}>EN</button>
              <button onClick={() => setLang("es")} className={`px-3 py-1 rounded-full text-xs ${lang==='es'?'bg-[#8D869B] text-[#FDFAF3]':'text-[#5E7891]'}`}>ES</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
